import React, { useState, useRef, useEffect, useMemo, useCallback } from "react";
import { useShallow } from "zustand/react/shallow";

import { AllCommunityModule, type StateUpdatedEvent, type RowClickedEvent } from "ag-grid-community";
import { AgGridProvider } from "ag-grid-react";
const modules = [AllCommunityModule];

import { Input, Button, Select, MenuItem, Tooltip, FormControl } from "@mui/material";

import kutil, { type PageInfo } from "@modules/kutil";
import commonCode, { type Code } from "@modules/common_code";
import valueFormatter from "@modules/value_formatter";
import { useStableHandlers } from "@/modules/stable_handlers";

import { CustomAgGridReact, type CustomColDef, type CustomColGroupDef } from "@/components/aggrid/custom_ag_grid_react";

import router from "@user/router";
import { useTabsStore } from "@user/store/tabs_store";
import { useAuthStore } from "@user/store/auth_store";
import { useSettingsStore } from "@user/store/settings_store";

interface State {
  codeTypeCodes: Code[]
}

const defState: State = {
  codeTypeCodes: []
};

interface SearchParams {
  codeType: string,
  codeLike: string,
  valueLike: string,
  useYn: string,
  page: number,
  pageSize: number
}

const defSearchParams: SearchParams = {
  codeType: "",
  codeLike: "",
  valueLike: "",
  useYn: "Y",
  page: 1,
  pageSize: kutil.DEFAULT_PAGE_SIZE
};

interface Result {
  searchParams: SearchParams,
  pageInfo: PageInfo,
  infos: any[]
}

const defResult: Result = {
  searchParams: kutil.deepCopy(defSearchParams),
  pageInfo: {
    totalCount: 0,
    totalPage: 0,
    page: defSearchParams.page,
    pageSize: defSearchParams.pageSize
  },
  infos: []
};

function CodeList(props: any) {
  const [state, setState] = useState<State>(kutil.deepCopy(defState));
  const [searchParams, setSearchParams] = useState<SearchParams>(kutil.deepCopy(defSearchParams));
  const [result, setResult] = useState<Result>(kutil.deepCopy(defResult));

  const auth = useAuthStore(useShallow((state) => state.getAuth(props.id)));
  const location = useTabsStore((state) => state.getLocation(props.id));
  const gridState = useMemo(() => useSettingsStore.getState().getGridState(location.path), [location.path]);

  const searchParamsRef = useRef(searchParams);
  const updateSearchParams = useCallback((patch: Partial<SearchParams>, isReset: boolean = false) => {
    if (isReset) {
      searchParamsRef.current = { ...patch } as SearchParams;
    } else {
      searchParamsRef.current = { ...searchParamsRef.current, ...patch };
    }
    setSearchParams(searchParamsRef.current);
    return searchParamsRef.current;
  }, []);

  const gridInfo = useMemo((): (CustomColDef | CustomColGroupDef)[] => [
    { headerName: "대분류코드", headerClass: "hd-ta-left", field: "codeType", width: 250, cellStyle: { textAlign: "left", cursor: "pointer"} },
    { headerName: "대분류코드명", headerClass: "hd-ta-left", field: "codeTypeName", width: 250, cellStyle: { textAlign: "left", cursor: "pointer"}, valueFormatter: data=>commonCode.getCodeValue(state.codeTypeCodes, data.value) },
    { headerName: "코드", headerClass: "hd-ta-left", field: "code", width: 250, cellStyle: { textAlign: "left", cursor: "pointer"} },
    { headerName: "코드명", headerClass: "hd-ta-left", field: "value", width: 250, cellStyle: { textAlign: "left", cursor: "pointer"} },
    { headerName: "정렬순서", field: "sortOrder", width: 100, cellStyle: {textAlign: "center", cursor: "pointer"} },
    { headerName: "사용여부", field: "useYn", width: 100, cellStyle: {textAlign: "center", cursor: "pointer"}, valueFormatter: valueFormatter.commonCode("useYn") }
  ], []);

  ////////////////////////////////////////////////////////////////////////////////
  // grid 관련 기능들
  ////////////////////////////////////////////////////////////////////////////////
  const gridCallbacks = useStableHandlers({
    onStateUpdated: (e: StateUpdatedEvent) => {
      kutil.onStateUpdated(e, location.path);
    },
    onRowClicked: (e: RowClickedEvent) => {
      showDetail(e.data);
    }
  });

  // location 변경시 처리
  useEffect(() => {
    init();
  }, [location.key]);

  const isInit = useRef(false);
  async function init() {
    try {
      if (isInit.current == false) {
        isInit.current = await initData();
      }
      if (isInit.current) {
        await initParams();
        await searchInfos();
      }
    } finally {
      kutil.setLoadingStatus(false);
    }
  }

  async function initData() {
    try {
      const data = await kutil.sendGet("/api/system/code_list.data?mode=initData", {}, {
        reqFunction: init
      });
      setState(prev => ({ ...prev, codeTypeCodes: data.codeTypeCodes }));
      return true;
    } catch(e) {
      console.log(e);
      return false;
    }
  }

  async function initParams() {
    const searchParams = kutil.deepCopy(defSearchParams);
    kutil.copyObj(Object.fromEntries(new URLSearchParams(location.search).entries()), searchParams);
    updateSearchParams(searchParams);
  }

  async function searchInfos() {
    const searchParams = searchParamsRef.current;

    try {
      const reqSearchParams = kutil.copyObj(searchParams, {} as SearchParams);
      const data = await kutil.sendGet("/api/system/code_list.data?mode=search", reqSearchParams, {});
      setResult({
        searchParams: reqSearchParams,
        pageInfo: data.pageInfo,
        infos: data.infos,
      });
    } catch(e) {
      console.log(e);
    }
  }
  
  async function doSearch(_e: any, reqParams: any|null = null) {
    const searchParams = updateSearchParams({ ...reqParams, page: 1 });
    router.navigateByPath("/system/code_list", searchParams);
  }

  const goPage = useCallback((page: number) => {
    const searchParams = updateSearchParams({ ...result.searchParams, page }, true);
    router.navigateByPath("/system/code_list", searchParams);
  }, [result, router, updateSearchParams]);

  async function resetParams() {
    router.navigateByPath("/system/code_list", {});
  }

  function showDetail(info: any) {
    router.navigateByPath("/system/code_detail", {
      codeType: info.codeType,
      code: info.code,
      searchParams: JSON.stringify(result.searchParams)
    });
  }

  function addDetail() {
    router.navigateByPath("/system/code_detail", {
      codeType: "",
      code: "",
      searchParams: JSON.stringify(result.searchParams)
    });
  }

  return (
    <>
      {/* contents */}
      <div className="contents list">
        {/* contents 상단 */}
        <div className="contents-top">
          {/* 타이틀 영역 */}
          <div className="title-area">
            {/* 타이틀 */}
            <p>공통코드</p>
          </div>
          {/*// 타이틀 영역 */}
          {/* 버튼 영역 */}
          <div className="top-btn-area">
            <Button variant="outlined" onClick={doSearch}>조회</Button>
            { auth.write &&
            <Button variant="contained" onClick={addDetail}>신규등록</Button>
            }
          </div>
          {/*// 버튼 영역 */}
        </div>
        {/*// contents 상단 */}
        {/* 검색 */}
        <div className="srch-area">
          <div>
            <p>대분류코드</p>
            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
              <Select
                value={searchParams.codeType}
                onChange={(e) => {doSearch(e, {codeType: e.target.value});}}
                displayEmpty
              >
                <MenuItem value="">전체</MenuItem>
                {(state.codeTypeCodes as any[]).map((code) => (
                  <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <div>
            <p>코드</p>
            <Input
              inputProps={{ maxLength: 100 }}
              value={searchParams.codeLike}
              onChange={(e) => {updateSearchParams({ codeLike: e.target.value })}}
              onKeyDown={(e) => {kutil.onKeyDownForInput(e, doSearch)}}
              placeholder="코드"
            />
          </div>
          <div>
            <p>코드명</p>
            <Input
              inputProps={{ maxLength: 100 }}
              value={searchParams.valueLike}
              onChange={(e) => {updateSearchParams({ valueLike: e.target.value })}}
              onKeyDown={(e) => {kutil.onKeyDownForInput(e, doSearch)}}
              placeholder="코드명"
            />
          </div>
          <div>
            <p>사용여부</p>
            <FormControl sx={{ m: 1, minWidth: 120 }} size="small">
              <Select
                value={searchParams.useYn}
                onChange={(e) => {doSearch(e, {useYn: e.target.value});}}
                displayEmpty
              >
                <MenuItem value="">전체</MenuItem>
                {(commonCode.useYn as any[]).map((code) => (
                  <MenuItem key={code.code} value={code.code}>{code.value}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </div>
          <p>
            <Tooltip title="초기화">
              <button className="icon-button form-reset" onClick={resetParams}>초기화</button>
            </Tooltip>
          </p>
        </div>
        {/*// 검색 */}
        {/* 목록 상단 정보
        <div className="list_top_area">
          <span><em>{ result.pageInfo.totalCount }</em>건</span>
        </div>
        목록 상단 정보 */}
        {/* 목록 */}
        <AgGridProvider modules={modules}>
          <div className="grid-area">
            <CustomAgGridReact
              columnDefs={gridInfo}
              rowData={result.infos}
              defaultColDef={kutil.defaultColDef}
              initialState={gridState}
              onStateUpdated={gridCallbacks.onStateUpdated}
              onRowClicked={gridCallbacks.onRowClicked}
            />
          </div>
        </AgGridProvider>
        {/*// 목록 */}
      </div>
      {/*// contents */}
    </>
  );
}

export default React.memo(CodeList);
